import { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';

type NavigateFn = (...args: unknown[]) => void;
type WritableNavigator = { push: NavigateFn; replace: NavigateFn };

const pathnameOf = (to: unknown): string | undefined => {
  if (typeof to === 'string') return to.split(/[?#]/)[0] || undefined;
  if (to && typeof to === 'object') {
    return (to as { pathname?: string }).pathname || undefined;
  }
  return undefined;
};

const searchOf = (to: unknown): string => {
  if (typeof to === 'string') return to.split('#')[0].split('?')[1] ?? '';
  if (to && typeof to === 'object') {
    return ((to as { search?: string }).search ?? '').replace(/^\?/, '');
  }
  return '';
};

// Asks before an in-app navigation leaves this page while `when` is true.
//
// The app mounts <BrowserRouter>, not a data router, so react-router's
// useBlocker / unstable_usePrompt are not available. Instead this wraps the
// router navigator's push and replace while `when` is true, and puts the
// originals back as soon as it is false or the page unmounts.
//
// Limits:
// - Navigations that stay on this pathname (the page's own tab, message and
//   language changes) pass through: the page guards those itself. Not a bare
//   link to the page (the sidebar's own item): it resets the tab and unmounts
//   the editor, so it asks like a link away (review D-02). The page's own
//   guarded change to the bare page goes through `allow`.
// - Browser Back / Forward (popstate) and typing a URL cannot be blocked this
//   way. The beforeunload warning covers closing or reloading the tab only.
const useNavigationGuard = (when: boolean) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { pathname, search } = useLocation();
  const here = useRef({ pathname, search });
  here.current = { pathname, search };
  const allowed = useRef(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!when) return undefined;
    const nav = navigator as unknown as WritableNavigator;
    const { push, replace } = nav;
    const guard =
      (original: NavigateFn): NavigateFn =>
      (...args) => {
        const target = pathnameOf(args[0]);
        const resets =
          target === here.current.pathname &&
          searchOf(args[0]) === '' &&
          here.current.search !== '';
        if (
          allowed.current ||
          target === undefined ||
          (target === here.current.pathname && !resets)
        ) {
          original.apply(nav, args);
          return;
        }
        setPending(() => () => original.apply(nav, args));
      };
    nav.push = guard(push);
    nav.replace = guard(replace);
    return () => {
      nav.push = push;
      nav.replace = replace;
    };
  }, [navigator, when]);

  return {
    pending: pending !== null,
    proceed: () => {
      const go = pending;
      setPending(null);
      go?.();
    },
    stay: () => setPending(null),
    // Runs a navigation the page already confirmed, without asking again.
    allow: (go: () => void) => {
      allowed.current = true;
      try {
        go();
      } finally {
        allowed.current = false;
      }
    },
  };
};

export default useNavigationGuard;
